import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";

import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import GradientLineChart from "examples/Charts/LineCharts/GradientLineChart";
import HorizontalBarChart from "examples/Charts/BarCharts/HorizontalBarChart";
import ReportsDoughnutChart from "examples/Charts/DoughnutCharts/ReportsDoughnutChart";
import RadarChart from "examples/Charts/RadarChart";
import MiniGradientLineChart from "examples/Charts/LineCharts/MiniGradientLineChart";

import MetricHeroCard from "./components/MetricHeroCard";
import SectionHeading from "./components/SectionHeading";
import ConversionFunnel from "./components/ConversionFunnel";
import MissionDensityMap from "./components/MissionDensityMap";
import OperatingStatesPanel from "./components/OperatingStatesPanel";
import AirportOperationsSection from "./components/AirportOperationsSection";

import { ANALYTICS_PERSONA, getPersonaDashboardConfig } from "./config/analyticsPersonas";
import {
  getHeroMetricsForKeys,
  impactOverTimeChart,
  missionCategoryChart,
  declineDoughnut,
  pilotReliabilityRadar,
  nonprofitReliabilityRadar,
  conversionFunnel,
  churnTrendChart,
  repeatPilotChart,
  missionHeatPoints,
  US_STATE_CODES,
  operatingStateCoverageByFilter,
  airportMissionRows,
  orgsSupportedThisMonth,
} from "./data/analyticsDummyData";

const chartCardSx = {
  "& .MuiCard-root": {
    borderRadius: "1.25rem",
    border: "none",
    boxShadow: "0 4px 24px rgba(52, 71, 103, 0.07)",
    overflow: "hidden",
    height: "100%",
  },
};

function NetworkTownCallout() {
  return (
    <Card
      sx={{
        height: "100%",
        borderRadius: "1.25rem",
        border: "none",
        boxShadow: "0 4px 24px rgba(52, 71, 103, 0.07)",
        background: (theme) =>
          `linear-gradient(145deg, ${theme.palette.common.white}, ${theme.palette.grey[100]})`,
      }}
    >
      <SoftBox p={{ xs: 3, md: 3.5 }}>
        <SoftTypography variant="h6" fontWeight="bold" mb={1.25}>
          Towns & agencies
        </SoftTypography>
        <SoftTypography variant="body2" color="text" sx={{ lineHeight: 1.7 }}>
          Use fill-rate and category trends to plan coordination. Maps below show where nonprofits are active
          (sample data).
        </SoftTypography>
      </SoftBox>
    </Card>
  );
}

function RepeatPilotPanel() {
  return (
    <Card
      sx={{
        borderRadius: "1.25rem",
        border: "none",
        boxShadow: "0 4px 24px rgba(52, 71, 103, 0.07)",
        height: "100%",
        background: (theme) =>
          `linear-gradient(160deg, ${theme.palette.common.white}, ${theme.palette.grey[100]})`,
      }}
    >
      <SoftBox p={{ xs: 3, md: 3.5 }}>
        <SoftTypography variant="h6" fontWeight="bold" mb={1}>
          Repeat pilot count
        </SoftTypography>
        <SoftTypography variant="caption" color="text" display="block" mb={2.5} sx={{ lineHeight: 1.5 }}>
          Pilots who flew more than one mission (rolling year)
        </SoftTypography>
        <SoftTypography variant="h3" fontWeight="bold" color="primary" mb={2.5}>
          156
        </SoftTypography>
        <SoftTypography variant="caption" color="text" fontWeight="medium" display="block" mb={1.25}>
          Quarterly trend
        </SoftTypography>
        <MiniGradientLineChart title="" description="" chart={repeatPilotChart} height="6rem" />
      </SoftBox>
    </Card>
  );
}

function ThroughputSection({ persona, sections }) {
  const hasImpact = sections.impactOverTime;
  const hasCategories = sections.missionCategoryTags;
  if (!hasImpact && !hasCategories) return null;

  return (
    <>
      <SectionHeading
        first
        eyebrow="Throughput"
        title="Impact & categories"
        subtitle={
          hasCategories
            ? "Mission volume, lives helped, and mix by category."
            : "Missions flown and estimated lives impacted over time."
        }
      />
      <Grid container spacing={3} sx={chartCardSx}>
        {/* Pilot: impact + nonprofit reliability side-by-side */}
        {persona === ANALYTICS_PERSONA.PILOT && hasImpact && (
          <>
            <Grid item xs={12} lg={8}>
              <GradientLineChart
                title="Impact over time"
                description="Monthly missions flown and estimated lives impacted."
                height="20rem"
                chart={impactOverTimeChart}
              />
            </Grid>
            <Grid item xs={12} lg={4}>
              <RadarChart
                title="Nonprofit reliability"
                description="Responsiveness and coordination for organizations you fly with (dummy)."
                chart={nonprofitReliabilityRadar}
              />
            </Grid>
          </>
        )}

        {/* Airport: impact + repeat pilots */}
        {persona === ANALYTICS_PERSONA.AIRPORT && hasImpact && sections.repeatPilots && (
          <>
            <Grid item xs={12} lg={8}>
              <GradientLineChart
                title="Impact over time"
                description="Monthly missions flown and estimated lives impacted."
                height="20rem"
                chart={impactOverTimeChart}
              />
            </Grid>
            <Grid item xs={12} lg={4}>
              <RepeatPilotPanel />
            </Grid>
          </>
        )}

        {/* Default throughput: admin, nonprofit, network */}
        {persona !== ANALYTICS_PERSONA.PILOT &&
          persona !== ANALYTICS_PERSONA.AIRPORT &&
          (hasImpact || hasCategories) && (
            <>
              {hasImpact && (
                <Grid item xs={12} lg={hasCategories ? 8 : 12}>
                  <GradientLineChart
                    title="Impact over time"
                    description="Monthly missions flown and estimated lives impacted."
                    height="20rem"
                    chart={impactOverTimeChart}
                  />
                </Grid>
              )}
              {hasCategories && (
                <Grid item xs={12} lg={4}>
                  <HorizontalBarChart
                    title="Mission category tags"
                    description="Count of missions by type (sample period)."
                    height="20rem"
                    chart={missionCategoryChart}
                  />
                </Grid>
              )}
            </>
          )}
      </Grid>
    </>
  );
}

function SecondRowSection({ persona, sections }) {
  if (
    persona === ANALYTICS_PERSONA.AIRPORT ||
    persona === ANALYTICS_PERSONA.PILOT ||
    persona === ANALYTICS_PERSONA.NONPROFIT
  ) {
    return null;
  }

  const showDecline = sections.declineDoughnut;
  const showRepeat = sections.repeatPilots;

  if (!showDecline && !showRepeat) return null;

  return (
    <Grid container spacing={3} mt={2} sx={chartCardSx}>
      {showDecline && (
        <Grid item xs={12} md={persona === ANALYTICS_PERSONA.NETWORK ? 6 : 6}>
          <ReportsDoughnutChart
            title="Mission fill outcomes"
            tooltip="Share of requested missions that were flown vs declined or expired unfilled."
            count={{ number: "1.2k", text: "requests (90d)" }}
            chart={{
              labels: declineDoughnut.labels,
              datasets: declineDoughnut.datasets,
            }}
          />
        </Grid>
      )}
      {persona === ANALYTICS_PERSONA.NETWORK && showDecline && (
        <Grid item xs={12} md={6}>
          <NetworkTownCallout />
        </Grid>
      )}
      {showRepeat && persona !== ANALYTICS_PERSONA.NETWORK && (
        <Grid item xs={12} md={showDecline ? 6 : 12}>
          <RepeatPilotPanel />
        </Grid>
      )}
    </Grid>
  );
}

function NonprofitSecondRow({ sections }) {
  if (!sections.repeatPilots || !sections.pilotReliabilityRadar) return null;
  return (
    <Grid container spacing={3} mt={2} sx={chartCardSx}>
      <Grid item xs={12} md={6}>
        <RepeatPilotPanel />
      </Grid>
      <Grid item xs={12} md={6}>
        <RadarChart
          title="Pilot reliability"
          description="Operational quality for pilots supporting your programs (dummy)."
          chart={pilotReliabilityRadar}
        />
      </Grid>
    </Grid>
  );
}

/**
 * @param {{ persona?: string }} props
 */
function AnalyticsDashboardContent({ persona = ANALYTICS_PERSONA.NETWORK }) {
  const { sections, intro, heroKeys } = getPersonaDashboardConfig(persona);
  const heroes = getHeroMetricsForKeys(heroKeys);
  const livesSum = airportMissionRows.reduce((acc, r) => acc + r.lives, 0);

  const showReliabilityRow =
    sections.pilotReliabilityRadar || sections.nonprofitReliabilityRadar;
  const pilotHasRadarInThroughput =
    persona === ANALYTICS_PERSONA.PILOT && sections.nonprofitReliabilityRadar;
  const showReliabilitySection =
    showReliabilityRow &&
    !pilotHasRadarInThroughput &&
    !(persona === ANALYTICS_PERSONA.NONPROFIT && sections.pilotReliabilityRadar);

  const showGrowthRow = sections.conversionFunnel || sections.churnChart;
  const showGeoRow = sections.heatMap || sections.operatingStatesMap;

  return (
    <SoftBox>
      <SoftBox
        mb={{ xs: 4, md: 5 }}
        p={{ xs: 3, sm: 3.5, md: 4 }}
        borderRadius="xl"
        sx={{
          background: (theme) =>
            `linear-gradient(125deg, ${theme.palette.grey[100]} 0%, ${theme.palette.common.white} 45%, rgba(23, 193, 232, 0.06) 100%)`,
          border: "none",
          boxShadow: "0 2px 20px rgba(52, 71, 103, 0.06)",
        }}
      >
        <SoftTypography variant="overline" color="info" fontWeight="bold" letterSpacing="0.12em">
          {intro.eyebrow}
        </SoftTypography>
        <SoftTypography variant="h4" fontWeight="bold" color="dark" mt={0.75} mb={1.5} sx={{ lineHeight: 1.25 }}>
          {intro.title}
        </SoftTypography>
        <SoftTypography variant="body2" color="text" sx={{ maxWidth: "42rem", lineHeight: 1.65 }}>
          {intro.subtitle} Sample data is shown until APIs are connected.
        </SoftTypography>
      </SoftBox>

      <Grid container spacing={3} mb={{ xs: 2, md: 3 }}>
        {heroes.map((m) => (
          <Grid item xs={12} sm={6} lg={3} key={m.key}>
            <MetricHeroCard
              label={m.label}
              value={m.value}
              sub={m.sub}
              accent={m.accent}
              icon={m.icon}
            />
          </Grid>
        ))}
      </Grid>

      <ThroughputSection persona={persona} sections={sections} />

      <SecondRowSection persona={persona} sections={sections} />

      {persona === ANALYTICS_PERSONA.NONPROFIT && <NonprofitSecondRow sections={sections} />}

      {showReliabilitySection && (
        <>
          <SectionHeading
            eyebrow="Reliability"
            title={
              sections.pilotReliabilityRadar && sections.nonprofitReliabilityRadar
                ? "Pilot & nonprofit quality"
                : sections.pilotReliabilityRadar
                  ? "Pilot reliability"
                  : "Nonprofit reliability"
            }
            subtitle="Composite scores (illustrative) for coordination and execution."
          />
          <Grid container spacing={3} sx={chartCardSx}>
            {sections.pilotReliabilityRadar && (
              <Grid item xs={12} md={sections.nonprofitReliabilityRadar ? 6 : 12}>
                <RadarChart
                  title="Pilot reliability"
                  description="Normalized scores across operational dimensions (dummy)."
                  chart={pilotReliabilityRadar}
                />
              </Grid>
            )}
            {sections.nonprofitReliabilityRadar && (
              <Grid item xs={12} md={sections.pilotReliabilityRadar ? 6 : 12}>
                <RadarChart
                  title="Nonprofit reliability"
                  description="Responsiveness and coordination quality (dummy)."
                  chart={nonprofitReliabilityRadar}
                />
              </Grid>
            )}
          </Grid>
        </>
      )}

      {showGrowthRow && (
        <>
          <SectionHeading
            eyebrow="Growth"
            title="Conversion & retention"
            subtitle="Signup-to-mission funnel and inactive-account churn."
          />
          <Grid container spacing={3} sx={chartCardSx}>
            {sections.conversionFunnel && (
              <Grid item xs={12} md={5}>
                <ConversionFunnel steps={conversionFunnel} />
              </Grid>
            )}
            {sections.churnChart && (
              <Grid item xs={12} md={sections.conversionFunnel ? 7 : 12}>
                <GradientLineChart
                  title="Account churn rate"
                  description="Percentage of accounts inactive 90+ days (6-month view)."
                  height="18rem"
                  chart={churnTrendChart}
                />
              </Grid>
            )}
          </Grid>
        </>
      )}

      {showGeoRow && (
        <>
          <SectionHeading
            eyebrow="Geography"
            title="Maps & coverage"
            subtitle="Mission density and nonprofit operating areas by type."
          />
          <Grid container spacing={3}>
            {sections.heatMap && (
              <Grid item xs={12} lg={sections.operatingStatesMap ? 6 : 12}>
                <MissionDensityMap points={missionHeatPoints} />
              </Grid>
            )}
            {sections.operatingStatesMap && (
              <Grid item xs={12} lg={sections.heatMap ? 6 : 12}>
                <OperatingStatesPanel
                  stateCodes={US_STATE_CODES}
                  coverageByFilter={operatingStateCoverageByFilter}
                />
              </Grid>
            )}
          </Grid>
        </>
      )}

      {sections.airportOperations && (
        <>
          <SectionHeading
            eyebrow="Airport"
            title="Field operations"
            subtitle="Missions, lives, date range filter (demo), and organizations you supported this month."
          />
          <AirportOperationsSection
            missionRows={airportMissionRows}
            organizations={orgsSupportedThisMonth}
            livesTotal={livesSum}
          />
        </>
      )}
    </SoftBox>
  );
}

export default AnalyticsDashboardContent;
